import {
  alpha,
  Avatar,
  Badge,
  Box,
  Card,
  CardContent,
  Container,
  Divider,
  Fab,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Toolbar,
  Typography,
  useTheme,
} from "@mui/material";
import { ArrowBack, Edit, PersonAdd } from "@mui/icons-material";
import { stringAvatar } from "utils";
import FormActions from "components/FormActions";
import { SubmitHandler, useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { addParent as addParentApi, getRegions, getCountries } from "apis";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import staticCountries from "components/data";
import PasswordHints from "components/PasswordHints";

interface FormFields {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  mobile: string;
  line1: string;
  line2: string;
  city: string;
  country: string;
  postcode: string;
}

const schema = Yup.object().shape({
  firstName: Yup.string().required("This field is required"),
  lastName: Yup.string().required("This field is required"),
  email: Yup.string().email("Invalid email").required("This field is required"),
  password: Yup.string()
    .required("This field is required")
    .min(8, "Min 8 characters"),
  confirmPassword: Yup.string()
    .required("This field is required")
    .oneOf([Yup.ref("password")], "Passwords do not match"),
  mobile: Yup.string().required("This field is required"),
  line1: Yup.string().required("This field is required"),
  city: Yup.string().required("This field is required"),
  postcode: Yup.string().required("This field is required"),
});

const req = (label: string) => (
  <>
    {label} <span style={{ color: "#d32f2f" }}>*</span>
  </>
);

const AddParent: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormFields>({
    defaultValues: { country: "Algeria" },
    resolver: yupResolver(schema) as any,
  });
  const { enqueueSnackbar } = useSnackbar();
  const [submitting, setSubmitting] = useState(false);
  const [photo, setPhoto] = useState<File | undefined>();
  const [country, setCountry] = useState("Algeria");
  const [countries, setCountries] = useState<
    { code: string; name: string; displayName: string }[]
  >([]);
  const [regions, setRegions] = useState<
    { code: string; name: string; displayName?: string }[]
  >([]);
  const [regionLabel, setRegionLabel] = useState("City");

  useEffect(() => {
    getCountries().then((data) =>
      setCountries(
        data.length
          ? data
          : staticCountries.map((c: any) => ({ ...c, displayName: c.name })),
      ),
    );
  }, [i18n.language]);

  useEffect(() => {
    const countryEntry =
      countries.find((c) => c.name === country) ||
      staticCountries.find((c) => c.name === country);
    if (countryEntry) {
      getRegions(countryEntry.code).then((data) => {
        setRegions(data.regions || []);
        setRegionLabel(data.label || "City");
      });
    } else {
      setRegions([]);
      setRegionLabel("City");
    }
  }, [country, i18n.language]);

  const onSubmit: SubmitHandler<FormFields> = (data) => {
    setSubmitting(true);
    addParentApi({ ...data, photo })
      .then(() => {
        enqueueSnackbar(t("parents.added"), { variant: "success" });
        navigate("/parents");
      })
      .catch((err) => {
        enqueueSnackbar(err?.response?.data?.message || err.message, {
          variant: "error",
        });
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <>
      <Toolbar
        sx={{
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
          gap: 1.5,
        }}
      >
        <IconButton
          onClick={() => navigate("/parents")}
          size="small"
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: 2,
            '[dir="rtl"] &': { transform: "scaleX(-1)" },
          }}
        >
          <ArrowBack fontSize="small" />
        </IconButton>
        <PersonAdd sx={{ color: "primary.main", fontSize: 22 }} />
        <Typography variant="h6" fontWeight={600}>
          {t("parents.add")}
        </Typography>
      </Toolbar>

      <Container sx={{ py: 3, maxWidth: 600 }} maxWidth={false}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Card variant="outlined" sx={{ borderRadius: 3, mb: 3 }}>
            <CardContent>
              <Box display="flex" justifyContent="center" mb={3}>
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  badgeContent={
                    <Fab
                      size="small"
                      component="label"
                      color="primary"
                      sx={{ width: 32, height: 32, minHeight: 32 }}
                    >
                      <input
                        hidden
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          if (e.target.files?.length)
                            setPhoto(e.target.files[0]);
                        }}
                      />
                      <Edit sx={{ fontSize: 16 }} />
                    </Fab>
                  }
                >
                  <Avatar
                    src={photo ? URL.createObjectURL(photo) : undefined}
                    {...(!photo
                      ? stringAvatar(" ", {
                          width: 88,
                          height: 88,
                          fontSize: 32,
                          border: `3px solid ${theme.palette.divider}`,
                        })
                      : {
                          sx: {
                            width: 88,
                            height: 88,
                            border: `3px solid ${theme.palette.divider}`,
                          },
                        })}
                  />
                </Badge>
              </Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
                {t("labels.general_info")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label={req(t("inputs.first_name"))}
                    fullWidth
                    size="small"
                    error={!!errors.firstName}
                    helperText={errors.firstName?.message as string}
                    {...register("firstName")}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={req(t("inputs.last_name"))}
                    fullWidth
                    size="small"
                    error={!!errors.lastName}
                    helperText={errors.lastName?.message as string}
                    {...register("lastName")}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label={req(t("inputs.email"))}
                    fullWidth
                    size="small"
                    type="email"
                    error={!!errors.email}
                    helperText={errors.email?.message as string}
                    {...register("email")}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label={req(t("inputs.mobile"))}
                    fullWidth
                    size="small"
                    error={!!errors.mobile}
                    helperText={errors.mobile?.message as string}
                    {...register("mobile")}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label={req(t("inputs.password"))}
                    fullWidth
                    size="small"
                    type="password"
                    error={!!errors.password}
                    helperText={errors.password?.message as string}
                    {...register("password")}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label={req(t("inputs.confirmPassword"))}
                    fullWidth
                    size="small"
                    type="password"
                    error={!!errors.confirmPassword}
                    helperText={errors.confirmPassword?.message as string}
                    {...register("confirmPassword")}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <PasswordHints value={watch("password")} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ borderRadius: 3, mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
                {t("address")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label={req(t("inputs.address_line1"))}
                    fullWidth
                    size="small"
                    error={!!errors.line1}
                    helperText={errors.line1?.message as string}
                    {...register("line1")}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label={t("inputs.address_line2")}
                    fullWidth
                    size="small"
                    {...register("line2")}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={4}>
                  {regions.length > 0 ? (
                    <FormControl fullWidth size="small" error={!!errors.city}>
                      <InputLabel>{req(regionLabel)}</InputLabel>
                      <Select
                        label={req(regionLabel)}
                        defaultValue=""
                        {...register("city")}
                        sx={{ borderRadius: 2 }}
                      >
                        {regions.map((r) => (
                          <MenuItem key={r.code} value={r.name}>
                            {r.code} - {r.displayName || r.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.city && (
                        <FormHelperText>
                          {errors.city?.message as string}
                        </FormHelperText>
                      )}
                    </FormControl>
                  ) : (
                    <TextField
                      label={req(t("inputs.city"))}
                      fullWidth
                      size="small"
                      error={!!errors.city}
                      helperText={errors.city?.message as string}
                      {...register("city")}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  )}
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label={req(t("inputs.post_code"))}
                    fullWidth
                    size="small"
                    error={!!errors.postcode}
                    helperText={errors.postcode?.message as string}
                    {...register("postcode")}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{req(t("inputs.country"))}</InputLabel>
                    <Select
                      label={req(t("inputs.country"))}
                      value={country}
                      {...register("country", {
                        onChange: (e) => setCountry(e.target.value),
                      })}
                      sx={{ borderRadius: 2 }}
                    >
                      {countries.map((c) => (
                        <MenuItem key={c.code} value={c.name}>
                          {c.displayName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <FormActions
            onCancel={() => navigate("/parents")}
            submitLabel={t("buttons.add")}
            submitting={submitting}
          />
        </form>
      </Container>
    </>
  );
};

export default AddParent;
